import React from "react";
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  UseFormTrigger,
  useWatch,
} from "react-hook-form";
import parse from "date-fns/parse";
import {
  CheckboxController,
  Icons,
  Text,
  TimeController,
} from "@pushpress/shared-components";
import { AppointmentTypeForm } from "./AppointmentTypeDrawer.types";
import {
  AVAILABILITY_END_DEFAULT,
  AVAILABILITY_START_DEFAULT,
} from "./AppointmentTypeDrawer.utils";
import styles from "./AppointmentTypeAvailability.module.scss";

interface AppointmentTypeAvailabilityProps {
  control: Control<AppointmentTypeForm>;
  setValue: UseFormSetValue<AppointmentTypeForm>;
  trigger: UseFormTrigger<AppointmentTypeForm>;
}

function AppointmentTypeAvailability(props: AppointmentTypeAvailabilityProps) {
  const { control, setValue, trigger } = props;

  const { fields } = useFieldArray({
    control,
    name: "availability",
  });

  const availability = useWatch({
    control,
    name: "availability",
  });

  const handleCheckedToggle = (index: number, checked: boolean) => {
    if (checked) {
      setValue(`availability.${index}.startTime`, AVAILABILITY_START_DEFAULT);
      setValue(`availability.${index}.endTime`, AVAILABILITY_END_DEFAULT);
    } else {
      setValue(`availability.${index}.startTime`, "");
      setValue(`availability.${index}.endTime`, "");
    }
  };

  const handleStartTimeChange = () => {
    trigger("availability");
  };

  const validateEndTime = (value: string, index: number) => {
    const startTime = availability[index].startTime;
    if (!startTime) {
      return;
    }
    const startDate = parse(startTime, "hh:mm a", new Date());
    const endDate = parse(value, "hh:mm a", new Date());
    if (endDate <= startDate) {
      return "Less than start";
    }
  };

  return (
    <>
      {fields.map((field, index) => (
        <div key={field.id} className={styles.item}>
          <div className={styles.checked}>
            <CheckboxController
              label={field.dayOfTheWeek.slice(0, 3)}
              control={control}
              name={`availability.${index}.checked`}
              onToggle={(checked) => handleCheckedToggle(index, checked)}
            />
          </div>
          {availability?.[index].checked ? (
            <>
              <div className={styles.start}>
                <TimeController
                  label="Start"
                  control={control}
                  name={`availability.${index}.startTime`}
                  hideLabel
                  required
                  autoComplete="off"
                  valueType="12-hour"
                  minuteStep={5}
                  onValueChange={handleStartTimeChange}
                />
              </div>
              <div className={styles.sep}>
                <Icons.ArrowRight className={styles.arrow} />
              </div>
              <div className={styles.end}>
                <TimeController
                  label="End"
                  control={control}
                  name={`availability.${index}.endTime`}
                  hideLabel
                  required
                  autoComplete="off"
                  valueType="12-hour"
                  minuteStep={5}
                  validate={(value: string) => validateEndTime(value, index)}
                />
              </div>
            </>
          ) : (
            <div className={styles.unavailable}>
              <Text
                variant="body-lg"
                color="wolf-500"
                style={{ lineHeight: "32px" }}
              >
                Unavailable
              </Text>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export default AppointmentTypeAvailability;
